import { useCallback, useMemo } from 'react';
import { SizeButtonStyles } from '../SizeButton.style';

/**
 * Custom hook for SizeButton component business logic
 * Handles size selection, click events, CSS class generation, and theme processing
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.value - Size value (e.g., "S", "M", "L", "XL")
 * @param {boolean} params.available - Whether the size is available for selection
 * @param {string} params.index - Size index identifier
 * @param {string} params.selectedSize - Currently selected size value
 * @param {Function} params.onSizeChange - Callback function when size is changed
 * @param {Object} params.theme - Theme object for styling
 * @param {string} params.enhancedSizeSelectorClassName - Additional CSS class name
 *
 * @returns {Object} Hook return object
 * @returns {Function} returns.handleClick - Click event handler
 * @returns {boolean} returns.isSelected - Whether this size is currently selected
 * @returns {string} returns.buttonClassName - Complete CSS class string
 * @returns {Object} returns.themedStyles - Processed theme styles
 * @returns {string} returns.buttonId - Unique button ID for testing
 * @returns {boolean} returns.isDisabled - Whether button should be disabled
 */
const useSizeButton = ({
  value,
  available,
  index,
  selectedSize,
  onSizeChange,
  theme,
  enhancedSizeSelectorClassName = '',
}) => {
  // Click handler with useCallback for performance optimization
  const handleClick = useCallback(() => {
    if (available && onSizeChange) {
      onSizeChange(value, index);
    }
  }, [value, index, available, onSizeChange]);

  // Selection state logic with case-insensitive comparison
  const isSelected = useMemo(() => {
    return selectedSize && value.toUpperCase() === selectedSize.toUpperCase();
  }, [selectedSize, value]);

  // CSS class generation with all conditional logic
  const buttonClassName = useMemo(() => {
    const baseClasses = ['size-button', 'rebranded'];

    if (enhancedSizeSelectorClassName) {
      baseClasses.push(enhancedSizeSelectorClassName);
    }

    if (isSelected) {
      baseClasses.push('size-button-selected');
    }

    if (!available) {
      baseClasses.push('size-button-unavailable');
    }

    return baseClasses.join(' ');
  }, [enhancedSizeSelectorClassName, isSelected, available]);

  // Theme processing
  const themedStyles = useMemo(() => {
    return SizeButtonStyles(theme);
  }, [theme]);

  // Button ID for testing
  const buttonId = useMemo(() => {
    return `testId-sizeButton-${value}`;
  }, [value]);

  // Disabled state
  const isDisabled = useMemo(() => {
    return !available;
  }, [available]);

  return {
    handleClick,
    isSelected,
    buttonClassName,
    themedStyles,
    buttonId,
    isDisabled,
  };
};

export { useSizeButton };
export default useSizeButton;
